// ============================================================
// AssistantBarApp — React-Insel der AssistantBar (Dock + Panel + Voice-UI).
//
// Die gesamte Logik (Text-Chat, Gemini-Live-Voice, Verlauf, Formulare)
// lebt React-frei in components/assistant-bar/engine/ — diese Komponente
// rendert deren Zustand (useSyncExternalStore) mit den UNVERÄNDERTEN
// CSS-Klassen aus assistant-bar.css und registriert die wenigen
// imperativen Griffe (Fokus, Orb-Container, Mode-Popup) als EngineHost.
// ============================================================
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from '../../lib/useTranslation.ts';
import { getVoiceInteraction, setVoiceInteraction, type VoiceInteraction } from '../../../core/voiceInteraction.ts';
import type { AssistantEngine } from '../../../components/assistant-bar/engine/AssistantEngine.ts';
import { ChatLog } from './ChatLog.tsx';
import { HistoryPanel } from './HistoryPanel.tsx';
import { EmailFormOverlay } from './EmailFormOverlay.tsx';
import { CustomerFormOverlay } from './CustomerFormOverlay.tsx';
import { OutboundConfirmCard } from './OutboundConfirmCard.tsx';
import { useDockViewportLock } from './useDockViewportLock.ts';

// SVG icon-ha (mostaghel az utils, ta sabok bemanad) — identisch zum Vanilla-Markup.
const MicIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="8" y1="22" x2="16" y2="22" />
  </svg>
);
const SendIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const SidebarIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="9" y1="3" x2="9" y2="21" />
  </svg>
);
const TrashIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const _isMobile = () => window.matchMedia('(max-width: 768px)').matches;

export interface AssistantBarAppProps { engine: AssistantEngine }

export function AssistantBarApp({ engine }: AssistantBarAppProps) {
  useSyncExternalStore(engine.subscribe, engine.getVersion, engine.getVersion);
  const t = useTranslation();

  // collapse/expand-e noar-e dastyar (slide be rast)
  const [collapsed, setCollapsed] = useState(false);
  const [inputValue, setInputValue] = useState('');
  // placeholder kootah dar mobile (früher _setPlaceholder + resize-Listener)
  const [isMobile, setIsMobile] = useState(_isMobile);

  const dockRef = useRef<HTMLDivElement | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const micBtnRef = useRef<HTMLButtonElement | null>(null);
  const orbWrapRef = useRef<HTMLDivElement | null>(null);

  // context menu baraye delete
  const [histMenu, setHistMenu] = useState<{ id: string; x: number; y: number } | null>(null);

  // ── Modus-Toggle am Mikrofon (Desktop) ─────────────────────
  // Erscheint beim Start der Sprachsitzung für ein paar Sekunden über dem
  // Mic — ein Tap wechselt zwischen 'visual' (Assistent führt sichtbar durch
  // die App) und 'voice' (nur Stimme, UI erst wenn nötig). Der Start wartet
  // NICHT auf den Toggle: die Sitzung läuft schon im gespeicherten Modus;
  // ein Wechsel baut sie neu auf (Prompt/Tools stecken im Live-Token).
  const [modePopPos, setModePopPos] = useState<{ left: number; bottom: number } | null>(null);
  const [modeCur, setModeCur] = useState<VoiceInteraction>(getVoiceInteraction);
  const modePopTimer = useRef<number | null>(null);

  const hideModePop = useCallback(() => {
    setModePopPos(null);
    if (modePopTimer.current) { clearTimeout(modePopTimer.current); modePopTimer.current = null; }
  }, []);
  const showModePop = useCallback(() => {
    setModeCur(getVoiceInteraction());
    // Direkt über dem Mic verankern (fixed, Viewport-Koordinaten — die Leiste
    // ist zentriert, offsetLeft wäre relativ zur Bar und damit falsch)
    const btn = micBtnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setModePopPos({ left: Math.max(8, r.left), bottom: window.innerHeight - r.top + 10 });
    if (modePopTimer.current) clearTimeout(modePopTimer.current);
    modePopTimer.current = window.setTimeout(() => setModePopPos(null), 5000);
  }, []);

  // ── EngineHost: die wenigen imperativen Griffe in die UI ──
  useEffect(() => {
    engine.setHost({
      focusInput: () => { inputRef.current?.focus(); },
      showModePop,
      hideModePop,
      getOrbWrap: () => orbWrapRef.current,
    });
    return () => { engine.setHost(null); };
  }, [engine, showModePop, hideModePop]);

  // iOS keyboard handling: dock ra be visualViewport ghofl kon (mesl-e ChatGPT)
  useDockViewportLock(engine.panelOpen, dockRef, chatRef);

  // resize → placeholder mobile/desktop neu wählen (wie _setPlaceholder)
  useEffect(() => {
    const onResize = () => setIsMobile(_isMobile());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── globale Tastatur/Klick-Handler (wie früher zwei GETRENNTE
  // document-Listener — beide feuern beim selben Escape) ──
  useEffect(() => {
    // ESC -> bastan-e form (ham customer ham email agar baz bashad)
    const onKeyForms = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (engine.custForm) { engine.hideCustomerForm(); }
      else if (engine.emailForm && engine.emailForm.visible) { engine.hideEmailForm(); }
    };
    // Esc -> bastan-e panel
    const onKeyPanel = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && engine.panelOpen) engine.closePanel();
    };
    // ba ta'khir gush bede ta click-e Delete avval sabt shavad
    const onDocClick = () => setHistMenu(null);
    document.addEventListener('keydown', onKeyForms);
    document.addEventListener('keydown', onKeyPanel);
    document.addEventListener('click', onDocClick);
    return () => {
      document.removeEventListener('keydown', onKeyForms);
      document.removeEventListener('keydown', onKeyPanel);
      document.removeEventListener('click', onDocClick);
    };
  }, [engine]);

  // auto-grow ta saghf-e CSS (do khat)
  const autoGrowInput = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 48) + 'px';
  };

  const submitText = () => {
    const text = inputValue.trim();
    if (!text) return;
    setInputValue('');
    const el = inputRef.current;
    if (el) el.style.height = 'auto';
    engine.sendText(text);
  };

  // Der Mikrofon-Knopf ist ein SCHALTER, kein Startknopf. Vorher hing hier
  // direkt startVoice: ein zweiter Tipp baute eine ZWEITE Sitzung auf, während
  // die erste weiterlief — «ich drücke aufs Mikrofon und es hört trotzdem zu».
  // Jetzt beendet der zweite Tipp die laufende Sitzung (Mikrofon-Spur, Audio-
  // Kontexte und Verbindung werden in stopVoice geschlossen). Das Panel bleibt
  // offen — zum Schliessen gibt es den eigenen Knopf.
  const onMicClick = () => {
    if (engine.isVoiceOn()) { engine.stopVoice(); return; }
    engine.startVoice();
  };

  const pickMode = (next: VoiceInteraction) => {
    if (next === getVoiceInteraction()) { hideModePop(); return; }
    setVoiceInteraction(next);
    setModeCur(next);
    hideModePop();
    // Läuft eine Sitzung, gilt der neue Modus erst mit einer NEUEN Verbindung
    if (engine.isVoiceOn()) {
      engine.stopVoice();
      engine.startVoice();
    }
  };

  return (
    <>
      <div className={'ab-overlay' + (engine.panelOpen ? ' ab-open' : '')} onClick={() => engine.closePanel()} />
      <div ref={dockRef} className={'ab-dock' + (collapsed ? ' ab-collapsed' : '')}>
        <div
          className={'ab-panel' + (engine.panelOpen ? ' ab-open' : '') + (engine.histCollapsed ? ' ab-hist-collapsed' : '')}
          id="ab-panel"
        >
          <HistoryPanel engine={engine} onItemContextMenu={(id, x, y) => setHistMenu({ id, x, y })} />
          <div className="ab-main" id="ab-main">
            <div className="ab-head">
              <button className="ab-hist-btn" id="ab-hist-btn" title={t('assistant.toggleHistory')} onClick={() => engine.toggleHist()}>{SidebarIcon}</button>
              <button className="ab-close" id="ab-close" aria-label={t('common.close')} onClick={() => engine.closePanel()}>{'✕'}</button>
            </div>
            <ChatLog engine={engine} chatRef={chatRef} />
            <div className={'ab-voice' + (engine.panelView === 'voice' ? ' ab-show' : '')} id="ab-voice">
              {/* SiriWave (imperativ) zeichnet in DIESEN Container — React
                  rendert hier bewusst keine Kinder (engine/voice.ts verwaltet
                  Canvas-Erzeugung und -Abbau selbst). */}
              <div className="ab-orb-wrap" id="ab-orb-wrap" ref={orbWrapRef} />
              <div className="ab-voice-hint" id="ab-voice-hint">{engine.hint ?? t('assistant.listening')}</div>
              <button className="ab-voice-stop" id="ab-voice-stop" onClick={() => { engine.stopVoice(); engine.closePanel(); }}>{t('assistant.end')}</button>
            </div>
          </div>
        </div>
        <div className="ab-bar">
          <button
            ref={micBtnRef}
            className={'ab-mic' + (engine.micRecording ? ' ab-recording' : '') + (engine.micConnecting ? ' ab-connecting' : '')}
            id="ab-mic"
            title={t('assistant.micTitle')}
            onClick={onMicClick}
          >{MicIcon}</button>
          <textarea
            ref={inputRef}
            id="ab-input"
            className="ab-input"
            rows={1}
            value={inputValue}
            placeholder={isMobile ? t('assistant.inputPlaceholderShort') : t('assistant.inputPlaceholder')}
            onChange={(e) => { setInputValue(e.target.value); autoGrowInput(); }}
            onKeyDown={(e) => {
              // Enter = ersal, Shift+Enter = khat-e jadid
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitText(); }
            }}
            onFocus={() => { if (!engine.panelOpen) engine.openPanel('text'); }}
          />
          <button className="ab-send" id="ab-send" title={t('assistant.sendTitle')} onClick={submitText}>{SendIcon}</button>
          <button className="ab-collapse" id="ab-collapse" title={t('assistant.hide')} aria-label={t('assistant.hide')} onClick={() => setCollapsed(true)}>{'»'}</button>
        </div>
        <button className="ab-expand" id="ab-expand" title={t('assistant.assistant')} aria-label={t('assistant.open')} onClick={() => setCollapsed(false)}>{'«'}</button>
        <EmailFormOverlay engine={engine} />
        <CustomerFormOverlay engine={engine} />
        {/* «Erst zeigen, dann senden»: Kundennachrichten gehen NUR über diese
            Karte raus. Sie hängt hier und nicht in einer Shell, damit sie auf
            beiden Oberflächen erscheint — egal welcher Screen gerade offen ist.
            Sie rendert sich selbst als Portal, wenn ein Entwurf wartet. */}
        <OutboundConfirmCard />
        {histMenu && (
          <div
            className="ab-hist-menu"
            style={{ display: 'block', left: histMenu.x, top: histMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="ab-hist-del"
              onClick={(e) => {
                e.stopPropagation();
                engine.deleteChat(histMenu.id);
                setHistMenu(null);
              }}
            >{TrashIcon}<span>{t('common.delete')}</span></button>
          </div>
        )}
        <div
          className={'ab-mode-pop' + (modePopPos ? ' ab-mode-open' : '')}
          style={modePopPos ? { left: modePopPos.left, bottom: modePopPos.bottom } : undefined}
        >
          <button type="button" className={'ab-mode-opt' + (modeCur === 'voice' ? ' ab-mode-on' : '')} data-mode="voice" onClick={() => pickMode('voice')}>
            <span className="material-symbols-rounded" aria-hidden="true">mic</span>
            <span className="ab-mode-label">{t('assistant.modeVoice')}</span>
          </button>
          <button type="button" className={'ab-mode-opt' + (modeCur === 'visual' ? ' ab-mode-on' : '')} data-mode="visual" onClick={() => pickMode('visual')}>
            <span className="material-symbols-rounded" aria-hidden="true">screen_share</span>
            <span className="ab-mode-label">{t('assistant.modeScreen')}</span>
          </button>
        </div>
      </div>
    </>
  );
}
